import { lazy, Suspense } from "react";
import { flattenRoutes } from "../utils/routeUtils";
import Loader from "../components/Reusable/Loader";

/**
 * COMPONENT MAP GUIDE
 *
 * Add components here with lazy loading:
 * - Key: Must match 'component' value in routesConfig.js
 * - Value: lazy(() => import('path/to/Component'))
 *
 * Naming Convention:
 * - PascalCase for component names
 * - Match the actual component file name
 *
 * Example:
 * routesConfig: { component: 'UserProfile' }
 * componentMap: { UserProfile: lazy(() => import('../pages/UserProfile')) }
 */

const componentMap = {
  Dashboard: lazy(() => import("../pages/Dashboard")),

  // User Specific
  AllUsers: lazy(() => import("../pages/users/AllUsers")),
  CompanyUsers: lazy(() => import("../pages/users/CompanyUsers")),
  ReferralUsers: lazy(() => import("../pages/users/ReferralUsers")),
  VerifyUsers: lazy(() => import("../pages/users/VerifyUsers")),
  UserProfile: lazy(() => import("../pages/users/UserProfile")),

  CompanyProfile: lazy(() => import("../pages/CompanyProfile")),
  ClientDetails: lazy(() => import("../pages/ClientDetails")),

  Orders: lazy(() => import("../pages/Orders")),
  OrderDetails: lazy(() => import("../pages/UserOrder")),

  CreateOffer: lazy(() => import("../pages/CreateOffer")),
  ViewOffers: lazy(() => import("../pages/ViewOffers")),

  AddTeamMember: lazy(() => import("../pages/AddTeamMember")),

  Payments: lazy(() => import("../pages/Payments")),
  Payment: lazy(() => import("../pages/Payment")),

  // Chat Components
  FullChatView: lazy(() => import("../components/Chat/FullChatView")),
  OrderChat: lazy(() => import("../components/Chat/OrderChat")),

  // Settings Components
  PlatformSettings: lazy(() => import("../pages/ThemeSettings")),
  NotificationSettings: lazy(() => import("../pages/ThemeSettings")),
  ThemeSettings: lazy(() => import("../pages/ThemeSettings")),

  // Auth Components
  SignIn: lazy(() => import("../pages/auth/SignIn")),
  ForgotPassword: lazy(() => import("../pages/auth/ForgotPassword")),
  SetNewPassword: lazy(() => import("../pages/auth/SetNewPassword")),
  ChangePassword: lazy(() => import("../pages/auth/ChangePassword")),
  FirstLoginResetPasswordPage: lazy(
    () => import("../pages/auth/FirstLoginResetPasswordPage"),
  ),

  // Test Components
  UseConfirmationTesting: lazy(() => import("../hooks/useConfirmation")),
  TestReusableComponents: lazy(() => import("../pages/TestReusableComponents")),

  // Subscription Components
  SubscriptionOrders: lazy(() => import("../pages/SubscriptionOrders")),
  SubscriptionManagement: lazy(() => import("../pages/SubscriptionManagement")),
  SubscriptionPlanManagement: lazy(
    () => import("../pages/SubscriptionPlanManagement"),
  ),

  AIworklowOrders: lazy(() => import("../pages/AIworkflowOrders")),
  UserAiWorkFlowManagement: lazy(() => import("../pages/UserAiWorkFlowManagement")) ,

  // Admin Profile
  AdminProfile: lazy(() => import("../pages/Profile")),
  AddTeamMember: lazy(() => import("../pages/AddTeamMember")),
};

/**
 * Loading fallback component
 */
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader />
  </div>
);

/**
 * Component Not Found Error - throws error that will be caught by error boundary
 */
const ThrowComponentError = ({ componentName }) => {
  throw new Error(
    `Component "${componentName}" not found in componentMap. Please add it to the component mapping in routerGenerator.js`,
  );
};

/**
 * Generate admin routes with layout
 */
export const generateAdminRoutes = (routeConfig) => {
  const routes = flattenRoutes(routeConfig);

  return routes
    .map((route) => {
      const Component = componentMap[route.component];

      // If component not found, throw error that will be caught by error boundary
      if (!Component) {
        console.error(
          `Component "${route.component}" not found in componentMap`,
        );
        return {
          path: route.path,
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ThrowComponentError componentName={route.component} />
            </Suspense>
          ),
        };
      }

      return {
        path: route.path,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Component />
          </Suspense>
        ),
      };
    })
    .filter(Boolean);
};

/**
 * Generate public routes
 */
export const generatePublicRoutes = (routeConfig) => {
  return Object.values(routeConfig)
    .map((route) => {
      const Component = componentMap[route.component];

      if (!Component) {
        console.error(
          `Component "${route.component}" not found in componentMap`,
        );
        return null;
      }

      return {
        path: route.path,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Component />
          </Suspense>
        ),
      };
    })
    .filter(Boolean);
};
